import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import { itemListToProductGraph } from '@/lib/seo';
import ZRangeHero from './components/ZRangeHero';
import ModelVariants from './components/ModelVariants';
import CoreFeatures from './components/CoreFeatures';
import InteriorDesign from './components/InteriorDesign';
import SmartSystems from './components/SmartSystems';
import TechnicalDetails from './components/TechnicalDetails';
import UsageScenarios from './components/UsageScenarios';
import InstallationGuide from './components/InstallationGuide';
import Footer from '../home/components/Footer';

const zRangeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Z Series Future Living Capsules',
  description: 'Nexa Pods Z Series — space capsule-style living units for luxury accommodation and immersive stays. Z-15, Z-20, Z-25 models with panoramic windows and smart systems.',
  numberOfItems: 3,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Thing',
        name: 'Z-15 Compact',
        description: 'Compact space capsule for couples with queen bed, full bathroom, kitchenette and smart controls. Best for couples.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/z-range-details',
        image: '/images/ai-compact-space-capsule-pod-exterior-view--b95b80.webp',
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Thing',
        name: 'Z-20 Standard',
        description: 'Most popular model with queen bed, sofa bed, full bathroom, living area and entertainment system.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/z-range-details',
        image: '/images/ai-standard-space-capsule-pod-exterior-20-s-fa75a1.webp',
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Thing',
        name: 'Z-25 Deluxe',
        description: 'Maximum comfort with separate bedroom, luxury bathroom, full kitchen and premium finishes.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/z-range-details',
        image: '/images/ai-deluxe-space-capsule-pod-exterior-view-2-69152c.webp',
      },
    },
  ],
};

const zRangeProductsJsonLd = itemListToProductGraph(zRangeJsonLd, {
  canonicalPath: '/z-range-details',
  requireNumericPrice: true,
});

export default function ZRangeDetailsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="Z Series Future Living Capsules | Nexa Pods Australia"
        description="Discover Nexa Z Series future living capsules for premium accommodation, panoramic stays and hospitality projects in Melbourne and across Australia."
        canonicalPath="/z-range-details"
        keywords="Z Series living capsule, future living pod Australia, luxury accommodation pod Melbourne, space capsule hotel Kew VIC"
        jsonLd={zRangeProductsJsonLd}
      />
      <div className="bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] text-white min-h-screen w-full overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/images/b58bd9e3723b.webp" 
              alt="Space Capsule Living" 
              className="h-16 sm:h-20 lg:h-28 w-auto"
            />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <Link 
                to="/product-range"
                className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Products
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
              </Link>
              
              {productsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-auto">
                  <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      className="block px-6 py-3 text-base text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a href="#models" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Models</a>
            <a href="#applications" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Applications</a>
            <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
            <div className="px-4 py-6 space-y-4 w-full">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Home</Link>
              
              {/* Mobile Products Dropdown */}
              <div>
                <button 
                  onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                  className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {productsDropdownOpen && (
                  <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                )}
              </div>

              <a href="#models" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Models</a>
              <a href="#applications" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Applications</a>
              <Link to="/contact" className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      <ZRangeHero />
      <ModelVariants />
      <CoreFeatures />
      <InteriorDesign />
      <SmartSystems />
      <TechnicalDetails />
      <UsageScenarios />
      <InstallationGuide />
      <Footer />
    </div>
    </>
  );
}
